import FacilitiesRepository from './FacilitiesRepository'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import React, {ReactNode} from 'react'
import FacilityModel, {UpdateFacilityModelObserver} from './FacilityModel'
import {AsyncFunction} from '../shared/hoc/withAsyncCommunication'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import {Nullable} from '../../app/TypeHelpers'
import {Messages} from '../../Messages'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../styledComponents/adminPage/Links'
import FacilityForm from './AdminRegisterFacilityPage/FacilityForm'
import {EquipmentsRepository} from '../equipments/EquipmentsRepository'
import UpdateFacilityForOvernightRental from './UpdateFacilityForOvernightRental'
import UpdateFacilityForDurationRental from './UpdateFacilityForDurationRental'
import NatCondominiumRepo from '../../__NoAutomatedTests/components/condominium/repository/NatCondominiumRepo'

interface Props {
    condominiumCd: string
    facilityId: number
    natCondominiumRepo: NatCondominiumRepo
    equipmentsRepo: EquipmentsRepository
    facilitiesRepo: FacilitiesRepository
    facilityModel: FacilityModel
}

interface State {
    isEnableToRegister: boolean
}

class AdminEditFacilityPage extends React.Component<Props & AdminPagePropsSet, State>
    implements UpdateFacilityModelObserver {

    static displayName: string = 'AdminEditFacilityPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            isEnableToRegister: false,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
    }

    onDataSetStateChange(completeToCreate: boolean): void {
        this.setState({
            isEnableToRegister: completeToCreate,
        })
    }

    onBuildOvernightRentalFacility(updateFacility: UpdateFacilityForOvernightRental): Promise<void> {
        return this.updateFacility(
            this.props.facilitiesRepo.updateOvernightRentalFacility.bind(
                this.props.facilitiesRepo,
                this.props.condominiumCd,
                this.props.facilityId,
                updateFacility,
            ),
        )
    }

    onBuildDurationRentalFacility(updateFacility: UpdateFacilityForDurationRental): Promise<void> {
        return this.updateFacility(
            this.props.facilitiesRepo.updateDurationRentalFacility.bind(
                this.props.facilitiesRepo,
                this.props.condominiumCd,
                this.props.facilityId,
                updateFacility,
            ),
        )
    }

    async updateFacility(
        updateFacilityFunction: AsyncFunction<any, DetailsWithInformation<number>>,
    ): Promise<void> {
        let facilityAndInformation: Nullable<DetailsWithInformation<number>> = null

        await this.props.eventEmitter.showLoading()
        try {
            facilityAndInformation = await this.props.asyncCallFunction(
                updateFacilityFunction,
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('施設')},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        if (facilityAndInformation) {
            const {condominiumCd} = this.props
            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/facilities/${facilityAndInformation.details}`,
                {
                    state: {
                        initialMessage: {
                            title: facilityAndInformation.information.title,
                            messageLevel: facilityAndInformation.information.type,
                            detail: facilityAndInformation.information.message,
                        },
                    },
                },
            )
        }
    }

    handleRegisterOkClick(): void {
        this.props.eventEmitter.closeDialog()
        this.props.eventEmitter.clearBroadcastedErrors()
        this.props.facilityModel.build()
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    handleRegisterButtonClick(): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0010.errorLevel,
                message: Messages.IOPCC0010.toString('施設'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    renderFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={!this.state.isEnableToRegister}
                        onClick={this.handleRegisterButtonClick}
                    >
                        登録
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/facilities/${this.props.facilityId}`}>
                        施設詳細へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactNode {
        return (
            <>
                <PageMainArea>
                    <FacilityForm
                        updateFacilityModelObserver={this}
                        {...this.props}
                    />
                </PageMainArea>

                <PageFooterArea>
                    {this.renderFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(AdminEditFacilityPage)
